import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, DragOverlay, PointerSensor, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { useMemo, useState } from 'react';
import { agentCountLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui';
import { canDropEngagement } from '../lib/can-drop-engagement';
import { cardSummaryLine, formatEngagementStatus, } from '../lib/card-summary';
import { filterEngagementRows } from '../lib/filter-engagement-rows';
import { ColumnStageControls } from './stage-toolbar';
import { EngagementKanbanCard } from './engagement-kanban-card';
function KanbanColumn({ column, index, columnCount, metrics, placementMode, cards, busy, editingStages, dropHighlight, onOpenCard, onMoveStage, }) {
    const { setNodeRef, isOver } = useDroppable({
        id: column.stageCode,
        data: {
            type: 'engagement-column',
            stageCode: column.stageCode,
        },
    });
    const isManual = placementMode === 'MANUAL';
    const highlight = dropHighlight && isOver;
    return (_jsxs("section", { ref: setNodeRef, className: [
            'flex min-h-[12rem] min-w-[220px] flex-1 flex-col rounded-md border bg-card p-3',
            highlight
                ? 'border-primary bg-primary/5'
                : 'border-border',
        ].join(' '), children: [_jsx("header", { className: "mb-3 space-y-2", children: _jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("h3", { className: "truncate text-sm font-semibold", children: column.title }), _jsx("p", { className: "text-xs text-muted-foreground", children: agentCountLabel(cards.length) })] }), editingStages && onMoveStage ? (_jsx(ColumnStageControls, { index: index, columnCount: columnCount, title: column.title, busy: busy, onMove: onMoveStage })) : null] }) }), _jsx("ul", { className: "flex flex-1 flex-col gap-2", children: cards.map((card) => (_jsx(EngagementKanbanCard, { card: card, metrics: metrics, draggable: isManual && card.status === 'ACTIVE' && !busy, onOpen: () => onOpenCard(card) }, card.engagementId))) })] }));
}
export function EngagementKanban({ columns, metrics, placementMode, query, busy, editingStages, onOpenCard, onAdvanceDrop, onMoveStage, }) {
    const [activeCard, setActiveCard] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 6 },
    }));
    const visibleByStage = useMemo(() => {
        const map = new Map();
        for (const column of columns) {
            map.set(column.stageCode, filterEngagementRows(column.engagements, query));
        }
        return map;
    }, [columns, query]);
    const totalVisible = useMemo(() => {
        let count = 0;
        for (const cards of visibleByStage.values()) {
            count += cards.length;
        }
        return count;
    }, [visibleByStage]);
    const totalCards = useMemo(() => columns.reduce((sum, column) => sum + column.engagements.length, 0), [columns]);
    function handleDragStart(event) {
        const card = event.active.data.current?.card;
        setActiveCard(card ?? null);
    }
    async function handleDragEnd(event) {
        const card = event.active.data.current?.card;
        const sourceStageCode = event.active.data.current?.stageCode;
        const targetStageCode = typeof event.over?.id === 'string'
            ? event.over.id
            : event.over?.data.current?.stageCode;
        setActiveCard(null);
        if (!card || !sourceStageCode || !targetStageCode) {
            return;
        }
        if (!canDropEngagement({
            placementMode,
            card,
            sourceStageCode,
            targetStageCode,
            columns,
        })) {
            return;
        }
        await onAdvanceDrop(card, targetStageCode);
    }
    const dropTargetAllowed = useMemo(() => {
        if (!activeCard) {
            return null;
        }
        for (const column of columns) {
            if (canDropEngagement({
                placementMode,
                card: activeCard,
                sourceStageCode: activeCard.stageCode,
                targetStageCode: column.stageCode,
                columns,
            })) {
                return column.stageCode;
            }
        }
        return null;
    }, [activeCard, columns, placementMode]);
    if (columns.length === 0) {
        return null;
    }
    return (_jsxs("div", { className: "space-y-3", children: [totalCards > 0 && query.trim() && totalVisible === 0 ? (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: uiCopy.engagementTableNoMatches }), _jsx(AlertDescription, { children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u0435 \u0437\u0430\u043F\u0440\u043E\u0441 \u0438\u043B\u0438 \u043E\u0447\u0438\u0441\u0442\u0438\u0442\u0435 \u043F\u043E\u043B\u0435 \u043F\u043E\u0438\u0441\u043A\u0430." })] })) : null, _jsxs(DndContext, { sensors: sensors, onDragStart: handleDragStart, onDragEnd: (event) => {
                    void handleDragEnd(event);
                }, onDragCancel: () => setActiveCard(null), children: [_jsx("div", { className: "flex gap-3 overflow-x-auto pb-2", children: columns.map((column, index) => (_jsx(KanbanColumn, { column: column, index: index, columnCount: columns.length, metrics: metrics, placementMode: placementMode, cards: visibleByStage.get(column.stageCode) ?? [], busy: busy, editingStages: editingStages, dropHighlight: dropTargetAllowed === column.stageCode, onOpenCard: onOpenCard, onMoveStage: onMoveStage }, column.stageCode))) }), _jsx(DragOverlay, { children: activeCard ? (_jsxs("div", { className: "w-[200px] rounded-md border border-border bg-background px-3 py-2 shadow-md", children: [_jsx("div", { className: "truncate text-sm font-medium", children: activeCard.agentDisplayName }), _jsx("div", { className: "text-xs text-muted-foreground", children: formatEngagementStatus(activeCard.status) }), cardSummaryLine(activeCard, metrics) ? (_jsx("div", { className: "mt-0.5 truncate text-xs text-muted-foreground", children: cardSummaryLine(activeCard, metrics) })) : null] })) : null })] })] }));
}
